/**
 * useSkerpaContext — Builds the full context string for Skerpa from frontend stores.
 * No API calls. All data is already loaded in Zustand stores.
 * Updates reactively when user switches projects, views, or journey state changes.
 */

import { useMemo } from 'react'
import { useProjectStore } from '@/stores/projectStore'
import { useAuthStore } from '@/stores/authStore'
import { useJourneyStore } from '@/stores/journeyStore'
import { useSkerpaContextStore } from '@/stores/skerpaContextStore'
import { useSkerpaEntitySummary } from '@/hooks/useSkerpaEntitySummary'
import { useProactiveGuide } from '@/hooks/useProactiveGuide'
import { SKERPA_SYSTEM_PROMPT } from '@/lib/skerpa/systemPrompt'

export function useSkerpaContext(): string {
  const project = useProjectStore(s => s.currentProject)
  const user = useAuthStore(s => s.user)
  const entitySummary = useSkerpaEntitySummary()
  const activeView = useSkerpaContextStore(s => s.activeView)
  const viewTitle = useSkerpaContextStore(s => s.viewTitle)
  const pageContext = useSkerpaContextStore(s => s.pageContext)
  const { cards: guideCards } = useProactiveGuide()

  const framework = useJourneyStore(s => s.framework)
  const journey = useJourneyStore(s => s.journey)
  const getCurrentPhase = useJourneyStore(s => s.getCurrentPhase)
  const getActivePhases = useJourneyStore(s => s.getActivePhases)
  const getDomainScores = useJourneyStore(s => s.getDomainScores)
  const getOverallProgress = useJourneyStore(s => s.getOverallProgress)
  const isActivityCompleted = useJourneyStore(s => s.isActivityCompleted)

  return useMemo(() => {
    const sections: string[] = []

    // ── CURRENT CONTEXT (first, so Hermes sees it immediately) ──
    const contextParts: string[] = []
    if (user) contextParts.push(`User: ${user.name || user.email}`)
    if (project) contextParts.push(`Project: ${project.name}`)
    if (activeView && viewTitle) {
      const viewHints: Record<string, string> = {
        'workspace': 'Dashboard — help user understand overall progress and next steps.',
        'discovery': 'Discovery Workflow — help plan and execute customer discovery.',
        'discovery-assistant': 'AI Discovery Coach — guided discovery with AI assistance.',
        'interviewer': 'Interview Analysis — reviewing interview transcripts and extracting insights.',
        'ai-interview': 'AI Interviews — conducting or reviewing AI-assisted interviews.',
        'persona-workshop': 'Persona Workshop — guided persona building with JTBD methodology.',
        'persona-configurator': 'Persona Configurator — editing personas, jobs, pains, gains directly.',
        'competitor-map': 'Competitor Map — analyzing competitors, positioning, market gaps.',
        'market-segmentation': 'Market Segments — evaluating segments, scoring criteria, sizing, and beachhead selection.',
        'value-design': 'Value Design — mapping pains to solutions, crafting value proposition.',
        'canvas-market': 'Market Canvas — market segments, trends, customer environment.',
        'canvas-business': 'Business Model Canvas — revenue model, channels, costs, partners.',
        'canvas-solution': 'Solution Canvas — features, capabilities, technical approach.',
        'canvas': 'Strategy Canvas — strategic planning and visualization.',
        'sources': 'Document Analyzer — uploading and analyzing source documents.',
        'smartdocs': 'Smart Docs — AI-enhanced documents with entity detection and generation.',
        'docs': 'Documents — browsing and managing project documents.',
        'web': 'Web Sources — analyzing web pages and online content.',
        'solution-workspace': 'Solution Workspace — vision, PRD, specs with AI generation.',
        'requirements': 'Requirements — PRD document with sections (overview / functional / non-functional), epics, and stories. Help user structure, prioritize, and refine product requirements.',
        'solution-configurator': 'Solution Configurator — managing solution requirements tree.',
        'dual-space': 'Problem-Solution Map — linking problems to solutions with cross-links.',
        'analysis': 'Validation Analysis — PMF readiness, evidence gaps, validation scores.',
        'dedup': 'Deduplication — finding and merging duplicate entities.',
        'crm': 'Customers & Contacts — managing customer relationships.',
        'knowledge': 'Knowledge Base — browsing project knowledge and references.',
        'ontology': 'Ontology Explorer — browsing entity classes and relationships.',
        'graph': 'Graph Explorer — visualizing entity relationships.',
        'tree': 'Data Tree — hierarchical view of all project entities.',
        'settings': 'Settings — configuration and preferences.',
      }
      contextParts.push(`Current view: ${viewTitle}`)
      const hint = viewHints[activeView]
      if (hint) contextParts.push(`Context: ${hint}`)
    }
    if (contextParts.length > 0) {
      sections.push(`## ACTIVE CONTEXT (answer based on THIS context)\n${contextParts.join('\n')}`)
    }

    // ── Knowledge graph entities (single source of truth) ──
    if (entitySummary) {
      sections.push(`## PROJECT ENTITIES (from knowledge graph)\n${entitySummary}\n\nDo NOT suggest creating entities that already exist above.`)
    }

    // ── Visible page data (additional detail from active view) ──
    if (pageContext) {
      sections.push(`## WHAT'S ON SCREEN RIGHT NOW\n${pageContext}\n\nUse this data directly. Do NOT ask the user for information that is already shown above.`)
    }

    // ── Identity + thinking frame + response shape + scope + actions schema ──
    sections.push(SKERPA_SYSTEM_PROMPT)

    // (User, project, and view are already in ACTIVE CONTEXT above)

    // ── Journey ──
    if (framework && journey) {
      const currentPhase = getCurrentPhase()
      const phases = getActivePhases()
      const scores = getDomainScores()
      const overall = getOverallProgress()

      const phaseLines = phases.map(p => {
        const pct = journey.phaseProgress[p.id] || 0
        const isCurrent = p.id === journey.currentPhaseId
        return `  ${p.order}. ${p.name} (${p.domain}) — ${pct}%${isCurrent ? ' ← CURRENT' : ''}`
      }).join('\n')

      // Goal info — show selected goal or list available options
      const selectedGoal = framework.goals.find(g => g.id === journey.goalId)
      let goalSection: string
      if (selectedGoal) {
        goalSection = `Goal: ${selectedGoal.name}${selectedGoal.shortDescription ? ` — ${selectedGoal.shortDescription}` : ''}`
      } else {
        const goalOptions = framework.goals.map(g =>
          `  - "${g.name}": ${g.shortDescription || g.description || ''}${g.recommendedFor?.length ? ` (recommended for: ${g.recommendedFor.join(', ')})` : ''}`
        ).join('\n')
        goalSection = `Goal: NOT SET — the user must select a goal to unlock their guided journey.\n\nAvailable goals:\n${goalOptions}\n\nHelp the user choose a goal based on their project context. Reference the actual goal names above.`
      }

      let journeySection = `## Journey
Overall progress: ${overall}%
Domain scores: Market ${scores.market}% · Solution ${scores.solution}% · Business ${scores.business}%
${goalSection}

Phases:
${phaseLines}`

      // Current phase detail
      if (currentPhase) {
        const activities = currentPhase.activities || []
        const activityLines = activities.map(a => {
          const done = isActivityCompleted(a.id)
          return `  ${done ? '[x]' : '[ ]'} ${a.name}: ${a.description}`
        }).join('\n')

        const entities = currentPhase.entities || []
        const entityLines = entities.map(e =>
          `  - ${e.label} (min: ${e.minCount}, required: ${e.isRequired})`
        ).join('\n')

        const gate = currentPhase.gate
        const criteria = gate?.criteria || []

        journeySection += `

### Current Phase: ${currentPhase.name}
Objective: ${currentPhase.objective}
${currentPhase.description}

Activities:
${activityLines || '  (none)'}

Required entities:
${entityLines || '  (none)'}

Gate criteria to advance:
${criteria.map((c: string) => `  - ${c}`).join('\n') || '  (none)'}

Guide the user toward completing their current phase. Tell them what's missing and what to do next.`
      }

      sections.push(journeySection)
    }

    // ── Proactive guide suggestions (so AI knows what was shown) ──
    if (guideCards.length > 0) {
      const cardLines = guideCards.map((c, i) =>
        `${i + 1}. "${c.title}" — ${c.description}`
      ).join('\n')
      sections.push(`## PROACTIVE SUGGESTIONS (shown to user)\nThe following guide cards are displayed to the user:\n${cardLines}\n\nIf the user's message relates to one of these, provide a focused, actionable response for that topic. Do NOT repeat what the card already says.`)
    }

    return sections.join('\n\n')
  }, [user, project, entitySummary, activeView, viewTitle, pageContext, framework, journey, getCurrentPhase, getActivePhases, getDomainScores, getOverallProgress, isActivityCompleted, guideCards])
}
